export interface DMProcessStep {
  title: string;
  description: string;
}

export interface DMFaq {
  question: string;
  answer: string;
}

export interface DigitalMarketingService {
  id: string;
  slug: string;
  route: string;
  title: string;
  tagline: string;
  description: string;
  longDescription: string;
  icon: string;
  deliverables: string[];
  process: DMProcessStep[];
  tools: string[];
  faqs: DMFaq[];
  seoTitle: string;
  seoDescription: string;
  keywords: string;
}

export const DIGITAL_MARKETING_SERVICES: DigitalMarketingService[] = [
  {
    id: "seo-services",
    slug: "seo-services",
    route: "/seo-services",
    title: "SEO Services",
    tagline: "Rank higher, get found, and turn search into steady business.",
    description:
      "Technical fixes, content, and authority building that move your site up Google for the searches your buyers actually type.",
    longDescription:
      "Paid ads stop the day you stop paying. SEO compounds. We fix the technical foundations, target the keywords that signal buying intent, publish content that answers real questions, and build the authority Google rewards — so organic search becomes a reliable, growing source of enquiries.",
    icon: "Search",
    deliverables: [
      "Technical SEO audit and fixes",
      "Keyword research and mapping",
      "On-page optimization",
      "Content briefs and optimization",
      "Internal linking structure",
      "Monthly ranking and traffic reports",
    ],
    process: [
      { title: "Audit", description: "We crawl your site, check speed, structure, and indexing, and benchmark you against competitors." },
      { title: "Strategy", description: "We pick the keywords worth winning and map each one to a page built to convert." },
      { title: "Execute", description: "We fix technical issues, optimize pages, and publish content on a steady schedule." },
      { title: "Grow", description: "We track rankings and traffic monthly, then double down on what is working." },
    ],
    tools: ["Google Search Console", "Google Analytics", "Ahrefs", "Screaming Frog", "WordPress", "Shopify"],
    faqs: [
      { question: "How long does SEO take to show results?", answer: "Most sites see meaningful movement in 3 to 6 months. Technical fixes can lift traffic sooner, while competitive keywords take steady work over time." },
      { question: "Do you guarantee first-page rankings?", answer: "No honest agency can guarantee a specific position. We guarantee the work: audits, fixes, content, and transparent monthly reporting on rankings and traffic." },
      { question: "Is SEO better than paid ads?", answer: "They do different jobs. Ads buy immediate visibility; SEO builds an asset that keeps bringing traffic without paying per click. Most clients do best with both." },
    ],
    seoTitle: "SEO Services in India | Make Mind Automations",
    seoDescription:
      "Technical SEO, keyword strategy, and content that lift your Google rankings and turn organic search into steady enquiries. Serving businesses across India.",
    keywords: "SEO services, technical SEO, keyword research, on-page SEO, link building, organic traffic agency, SEO services India, SEO agency India",
  },
  {
    id: "local-seo",
    slug: "local-seo",
    route: "/local-seo",
    title: "Local SEO",
    tagline: "Be the first business nearby customers find.",
    description:
      "Google Business Profile, reviews, and local pages that put you in the map pack for 'near me' searches.",
    longDescription:
      "When someone nearby searches for what you sell, Google shows a map first. Local SEO makes sure your business is the one on it — with accurate details, strong reviews, and pages for every area you serve, so nearby customers call you instead of a competitor.",
    icon: "MapPin",
    deliverables: [
      "Google Business Profile setup and optimization",
      "Local citation building",
      "Review generation and responses",
      "Location and service-area pages",
      "Local keyword targeting",
      "Map pack ranking reports",
    ],
    process: [
      { title: "Audit", description: "We check your profile, listings, and reviews against the top-ranked competitors in your area." },
      { title: "Fix", description: "We correct listings, complete your profile, and add photos, services, and posts." },
      { title: "Build", description: "We create location pages and earn citations that prove where you operate." },
      { title: "Grow", description: "We run a steady review routine and track map rankings month after month." },
    ],
    tools: ["Google Business Profile", "Google Maps", "WhatsApp", "Google Analytics"],
    faqs: [
      { question: "What is the map pack?", answer: "The map pack is the box of three local businesses Google shows at the top of local searches, with a map. Most nearby customers pick from those three." },
      { question: "Do reviews really affect rankings?", answer: "Yes. Review count, recency, and responses all influence local rankings — and they strongly influence whether a customer calls you." },
      { question: "I serve several areas. Can you cover all of them?", answer: "Yes. We build dedicated pages and listings for each area you serve, so you can rank across your whole service region." },
    ],
    seoTitle: "Local SEO Services in India | Make Mind Automations",
    seoDescription:
      "Google Business Profile optimization, reviews, and local pages that put your business in the map pack for nearby customers. Serving businesses across India.",
    keywords: "local SEO, Google Business Profile, map pack, near me SEO, local citations, review management, local SEO India, Google Business Profile India",
  },
  {
    id: "social-media-marketing",
    slug: "social-media-marketing",
    route: "/social-media-marketing",
    title: "Social Media Marketing",
    tagline: "Content and community that turn followers into enquiries.",
    description:
      "Consistent, on-brand content and campaigns for Instagram, Facebook, LinkedIn, and YouTube — planned, posted, and measured.",
    longDescription:
      "Social media only works when it is consistent. We plan your content calendar, design reels and creatives, manage comments and messages, and run paid boosts behind your best posts — so your brand stays visible every week and followers turn into real enquiries.",
    icon: "Share2",
    deliverables: [
      "Monthly content calendars",
      "Reels, posts, and creatives",
      "Captions and hashtags",
      "Community management",
      "Paid social boosts",
      "Monthly performance reports",
    ],
    process: [
      { title: "Audit", description: "We review your profiles, content, and competitors to find what your audience actually responds to." },
      { title: "Plan", description: "We build a monthly calendar around your offers, seasons, and customer questions." },
      { title: "Publish", description: "We design, write, and post on schedule, and reply to comments and messages." },
      { title: "Grow", description: "We boost winning posts, track enquiries, and refine the mix every month." },
    ],
    tools: ["Instagram", "Facebook", "LinkedIn", "YouTube", "Canva", "Meta Business Suite"],
    faqs: [
      { question: "Which platforms should my business be on?", answer: "Wherever your customers spend time. For most local and consumer businesses that is Instagram and Facebook; for B2B it is LinkedIn. We recommend based on your buyers, not trends." },
      { question: "Do I need to be on camera?", answer: "It helps, but it is not required. We can build content around your products, customers, team, and text-based formats that still perform." },
      { question: "How do you measure success?", answer: "By enquiries and sales, not just likes. We track reach and engagement, but we report on the conversations and leads social actually produces." },
    ],
    seoTitle: "Social Media Marketing Services in India | Make Mind Automations",
    seoDescription:
      "Content calendars, reels, community management, and paid social campaigns that turn followers into enquiries. Serving businesses across India.",
    keywords: "social media marketing, Instagram marketing, Facebook marketing, LinkedIn marketing, content calendar, reels, social media marketing India, Instagram marketing India",
  },
  {
    id: "google-ads-ppc",
    slug: "google-ads-ppc",
    route: "/google-ads-ppc",
    title: "Google Ads & PPC",
    tagline: "Be at the top of Google from day one — with every rupee tracked.",
    description:
      "Search, shopping, and Performance Max campaigns built around buying-intent keywords, with conversion tracking on everything.",
    longDescription:
      "When someone searches for what you sell, they are ready to buy. Google Ads puts you in front of them at that exact moment. We build tightly themed campaigns, landing pages that convert, and tracking that shows exactly what each rupee returned — then optimize weekly toward cheaper leads and sales.",
    icon: "MousePointerClick",
    deliverables: [
      "Search campaign setup",
      "Shopping and Performance Max",
      "Keyword and negative keyword management",
      "Ad copy and extensions",
      "Landing page optimization",
      "Conversion tracking and weekly reports",
    ],
    process: [
      { title: "Audit", description: "We review your market, competitors, and past campaigns to find the keywords worth paying for." },
      { title: "Build", description: "We structure campaigns by intent, write ads that match the search, and set up conversion tracking." },
      { title: "Launch", description: "We launch with controlled budgets and watch search terms daily in the first weeks." },
      { title: "Optimize", description: "We cut wasted spend, test ads and pages, and shift budget to what converts cheapest." },
    ],
    tools: ["Google Ads", "Google Analytics", "Google Tag Manager", "Shopify", "WordPress"],
    faqs: [
      { question: "How much should I budget for Google Ads?", answer: "It depends on your market and cost per click. We recommend a starting budget after the audit, and we never spend without tracking what it returns." },
      { question: "How fast will I see results?", answer: "Traffic starts the day campaigns launch. Meaningful cost-per-lead improvements usually come within the first 4 to 6 weeks of optimization." },
      { question: "Do you also manage the landing pages?", answer: "Yes. Ads only work if the page converts. We review and optimize landing pages as part of every campaign." },
    ],
    seoTitle: "Google Ads & PPC Management in India | Make Mind Automations",
    seoDescription:
      "Search, shopping, and Performance Max campaigns with full conversion tracking and weekly optimization toward cheaper leads. Serving businesses across India.",
    keywords: "Google Ads, PPC management, search ads, shopping ads, Performance Max, conversion tracking, paid search, Google Ads India, PPC agency India",
  },
  {
    id: "meta-ads",
    slug: "meta-ads",
    route: "/meta-ads",
    title: "Meta Ads",
    tagline: "Facebook and Instagram ads built for leads and sales.",
    description:
      "Audience targeting, creative testing, and retargeting funnels that turn scrollers into customers — with ROAS reporting.",
    longDescription:
      "Meta ads work when the targeting, creative, and offer line up. We test audiences and creatives systematically, build retargeting funnels for people who already showed interest, and track results with proper pixel and Conversions API setup — so you know what each campaign actually returned.",
    icon: "Megaphone",
    deliverables: [
      "Audience and interest targeting",
      "Ad creatives and copy",
      "Retargeting funnels",
      "Lead forms and Conversions API",
      "A/B creative testing",
      "ROAS and lead reports",
    ],
    process: [
      { title: "Audit", description: "We check your pixel, past campaigns, and audience data to find what is worth scaling." },
      { title: "Build", description: "We design creatives and funnels for cold, warm, and hot audiences." },
      { title: "Launch", description: "We test several creatives and audiences with controlled budgets." },
      { title: "Optimize", description: "We scale winners, kill losers, and report on cost per lead and return on ad spend." },
    ],
    tools: ["Meta Ads Manager", "Instagram", "Facebook", "WhatsApp", "Canva"],
    faqs: [
      { question: "Are Meta ads good for lead generation?", answer: "Yes, when the funnel is right. Lead forms, retargeting, and WhatsApp follow-ups together convert far better than a single boosted post." },
      { question: "How much creative do I need?", answer: "More than one ad. We test multiple hooks, images, and formats, because creative is usually what decides whether a campaign works." },
      { question: "Can you track sales, not just leads?", answer: "Yes, with Conversions API and proper event setup we connect ad spend to purchases and real revenue, not just clicks." },
    ],
    seoTitle: "Meta Ads Services (Facebook & Instagram) in India | Make Mind Automations",
    seoDescription:
      "Facebook and Instagram ad campaigns with creative testing, retargeting funnels, and ROAS reporting. Serving businesses across India.",
    keywords: "Meta ads, Facebook ads, Instagram ads, retargeting, lead generation ads, ROAS, Conversions API, Meta ads India, Facebook ads India",
  },
  {
    id: "content-marketing",
    slug: "content-marketing",
    route: "/content-marketing",
    title: "Content Marketing",
    tagline: "Content that answers, convinces, and keeps ranking.",
    description:
      "SEO blogs, videos, and lead magnets that build authority, feed your sales conversations, and work for years.",
    longDescription:
      "Buyers research before they buy. Content marketing makes sure they find you during that research — with articles that answer their questions, videos that explain your offer, and guides that capture emails. Done right, each piece keeps working long after it is published.",
    icon: "PenLine",
    deliverables: [
      "SEO blog articles",
      "Video scripts and outlines",
      "Lead magnets and guides",
      "Content calendars",
      "Email and social repurposing",
      "Performance reports",
    ],
    process: [
      { title: "Research", description: "We map what your buyers search, ask, and worry about before buying." },
      { title: "Plan", description: "We build a calendar that covers every stage, from discovery to decision." },
      { title: "Create", description: "We write and design content in your voice, optimized to rank and convert." },
      { title: "Distribute", description: "We repurpose each piece across email and social so one article does the work of five." },
    ],
    tools: ["WordPress", "YouTube", "Google Docs", "Canva", "Google Analytics"],
    faqs: [
      { question: "How is content marketing different from SEO?", answer: "SEO is how people find you; content is what they find. They work together — content gives SEO pages to rank, and SEO brings readers to content." },
      { question: "Who writes the content?", answer: "We do, from a brief built on your expertise. You review before anything publishes, so it always sounds like you." },
      { question: "How often should we publish?", answer: "Consistency beats volume. Most clients do well with two to four quality pieces a month, published on a schedule we can sustain." },
    ],
    seoTitle: "Content Marketing Services in India | Make Mind Automations",
    seoDescription:
      "SEO blogs, videos, and lead magnets that build authority, answer buyer questions, and keep ranking for years. Serving businesses across India.",
    keywords: "content marketing, SEO blog writing, video scripts, lead magnets, content calendar, brand storytelling, content marketing India, blog writing India",
  },
  {
    id: "email-marketing",
    slug: "email-marketing",
    route: "/email-marketing",
    title: "Email Marketing",
    tagline: "Turn your list into repeat revenue.",
    description:
      "Newsletters, drip sequences, and automations that nurture leads and bring customers back — without spamming anyone.",
    longDescription:
      "Your email list is the one audience no algorithm controls. We design campaigns and automations — welcome series, abandoned cart recovery, post-purchase follow-ups, and regular newsletters — that keep you in touch, build trust, and drive repeat sales.",
    icon: "Mail",
    deliverables: [
      "Newsletter campaigns",
      "Welcome and drip sequences",
      "Abandoned cart recovery",
      "List segmentation",
      "Template design",
      "Deliverability and reports",
    ],
    process: [
      { title: "Audit", description: "We review your list, past campaigns, and deliverability to find quick wins." },
      { title: "Segment", description: "We split your list by behavior and interest so every email feels relevant." },
      { title: "Build", description: "We design templates and sequences for welcome, nurture, and sales moments." },
      { title: "Optimize", description: "We test subject lines and timing, and report on opens, clicks, and revenue." },
    ],
    tools: ["Mailchimp", "HubSpot", "Klaviyo", "WhatsApp", "Google Analytics"],
    faqs: [
      { question: "Is email marketing still effective?", answer: "Yes. Email consistently returns more per rupee than most channels, because it reaches people who already asked to hear from you." },
      { question: "Will you spam my list?", answer: "No. We send to engaged segments, respect unsubscribes, and protect your sender reputation — that is what keeps deliverability high." },
      { question: "Can email work with WhatsApp automation?", answer: "Yes. Email and WhatsApp together cover both inbox and chat, and we can automate follow-ups across both from one flow." },
    ],
    seoTitle: "Email Marketing Services in India | Make Mind Automations",
    seoDescription:
      "Newsletters, drip sequences, and automations that nurture leads, recover carts, and drive repeat revenue. Serving businesses across India.",
    keywords: "email marketing, newsletter campaigns, drip sequences, abandoned cart emails, list segmentation, deliverability, email marketing India",
  },
  {
    id: "analytics-and-cro",
    slug: "analytics-and-cro",
    route: "/analytics-and-cro",
    title: "Analytics & CRO",
    tagline: "More customers from the traffic you already have.",
    description:
      "GA4 setup, conversion audits, A/B tests, and funnel reports that show what is leaking — and fix it.",
    longDescription:
      "Most sites lose buyers to slow pages, confusing forms, and unclear offers. We set up analytics properly, audit where visitors drop off, and run experiments on headlines, pages, and flows — so the same ad spend and the same traffic produce more enquiries and sales.",
    icon: "BarChart3",
    deliverables: [
      "GA4 and Tag Manager setup",
      "Conversion tracking audits",
      "Funnel and drop-off reports",
      "Landing page A/B tests",
      "Monthly insight reports",
    ],
    process: [
      { title: "Measure", description: "We fix tracking first, so every decision is based on real numbers." },
      { title: "Find", description: "We audit funnels and pages to locate the biggest leaks." },
      { title: "Test", description: "We run controlled experiments on the changes most likely to lift conversions." },
      { title: "Scale", description: "We roll out winners and keep a monthly testing rhythm." },
    ],
    tools: ["Google Analytics", "Google Tag Manager", "Google Search Console", "Hotjar", "Shopify"],
    faqs: [
      { question: "What is CRO?", answer: "Conversion rate optimization: improving the share of visitors who enquire or buy, by testing pages, offers, and flows instead of just buying more traffic." },
      { question: "Do I need a lot of traffic for CRO?", answer: "You need enough to read results, but even small sites benefit from fixing obvious leaks like broken forms, slow pages, and unclear calls to action." },
      { question: "How is this different from a monthly report?", answer: "Reports describe the past. CRO changes the future: every report ends with experiments we actually run." },
    ],
    seoTitle: "Analytics & Conversion Rate Optimization in India | Make Mind Automations",
    seoDescription:
      "GA4 setup, funnel audits, and A/B testing that turn more of your existing traffic into enquiries and sales. Serving businesses across India.",
    keywords: "analytics, CRO, conversion rate optimization, GA4 setup, A/B testing, funnel analysis, landing page optimization, CRO India, GA4 setup India",
  },
];

export function getDigitalMarketingService(id: string): DigitalMarketingService | undefined {
  return DIGITAL_MARKETING_SERVICES.find((s) => s.id === id);
}
